import { create } from 'zustand';
import { 
  Board, 
  BoardListItem, 
  Card, 
  Column, 
  BoardParticipant, 
  User,
  WebSocketMessage,
  CardCreatedMessage,
  CardUpdatedMessage,
  CardDeletedMessage,
  CardVotedMessage,
  CardMovedMessage,
  CardVisibilityChangedMessage,
  WsCardPayload,
  UserJoinedMessage,
  UserLeftMessage,
  CursorMovedMessage,
  BoardLockedMessage,
  BoardUnlockedMessage,
} from '../types';
import { useAuthStore } from './authStore';

interface BoardState {
  // Board data
  currentBoard: Board | null;
  boards: BoardListItem[];
  loading: boolean;
  error: string | null;

  // Real-time data
  websocket: WebSocket | null;
  participants: Map<string, BoardParticipant>;
  cursors: Map<string, { x: number; y: number; user: User }>;

  // Celebration
  celebrationEvent: { mode: string; triggeredBy: string; id: number } | null;
  clearCelebration: () => void;
  
  // Actions
  fetchBoards: () => Promise<void>;
  fetchBoard: (boardId: string) => Promise<void>;
  createBoard: (data: Partial<Board>) => Promise<Board>;
  updateBoard: (boardId: string, data: Partial<Board>) => Promise<void>;
  deleteBoard: (boardId: string) => Promise<void>;
  joinBoard: (boardId: string) => Promise<void>;
  leaveBoard: (boardId: string) => Promise<void>;
  
  // WebSocket actions
  connectWebSocket: (boardId: string, ticket: string) => void;
  disconnectWebSocket: () => void;
  sendWebSocketMessage: (message: any) => void;
  
  // Card actions
  createCard: (columnId: string, content: string, position?: { x: number; y: number }) => void;
  updateCard: (cardId: string, data: Partial<Card>) => void;
  deleteCard: (cardId: string) => void;
  moveCard: (cardId: string, columnId: string, position: { x: number; y: number }, order: number) => void;
  voteCard: (cardId: string, voteValue: number) => void;
  
  // Canvas card actions (with proper async handling and REST fallback)
  createCanvasCard: (columnId: string, cardData: {
    content: string;
    position_x: number;
    position_y: number;
    width?: number;
    height?: number;
    z_index?: number;
    color?: string;
  }) => Promise<Card | null>;
  updateCanvasCard: (cardId: string, updates: {
    content?: string;
    position_x?: number;
    position_y?: number;
    width?: number;
    height?: number;
    z_index?: number;
    color?: string;
  }) => Promise<boolean>;
  
  toggleCardVisibility: (cardId: string) => Promise<boolean>;

  // Column actions
  createColumn: (boardId: string, data: Partial<Column>) => Promise<void>;
  updateColumn: (columnId: string, data: Partial<Column>) => Promise<void>;
  deleteColumn: (columnId: string) => Promise<void>;
  
  // Board controls
  toggleBoardLock: () => void;
  updateCursorPosition: (x: number, y: number) => void;
  
  // Utility actions
  clearError: () => void;
  reset: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';

export const useBoardStore = create<BoardState>((set, get) => ({
  // Initial state
  currentBoard: null,
  boards: [],
  loading: false,
  error: null,
  websocket: null,
  celebrationEvent: null,
  clearCelebration: () => set({ celebrationEvent: null }),
  participants: new Map(),
  cursors: new Map(),

  // Board CRUD operations
  fetchBoards: async () => {
    set({ loading: true, error: null });
    
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_BASE_URL}/boards/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch boards');
      }

      const data = await response.json();
      
      set({ 
        boards: data.results || data,
        loading: false 
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch boards',
        loading: false,
      });
    }
  },

  fetchBoard: async (boardId: string) => {
    set({ loading: true, error: null });
    
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_BASE_URL}/boards/${boardId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch board');
      }

      const board = await response.json();
      
      set({ 
        currentBoard: board,
        loading: false 
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch board',
        loading: false,
      });
    }
  },

  createBoard: async (data: Partial<Board>) => {
    set({ loading: true, error: null });
    
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_BASE_URL}/boards/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Extract error message from different possible formats
        let errorMessage = 'Failed to create board';
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (Array.isArray(errorData.non_field_errors)) {
          errorMessage = errorData.non_field_errors.join(' ');
        } else if (typeof errorData === 'object') {
          const firstKey = Object.keys(errorData)[0];
          if (firstKey) {
            const value = errorData[firstKey];
            errorMessage = Array.isArray(value) ? value.join(' ') : String(value);
          }
        }
        throw new Error(errorMessage);
      }

      const board = await response.json();
      
      set(state => ({ 
        boards: [board, ...state.boards],
        loading: false 
      }));

      return board;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create board',
        loading: false,
      });
      throw error;
    }
  },

  updateBoard: async (boardId: string, data: Partial<Board>) => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_BASE_URL}/boards/${boardId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to update board');
      }

      const updatedBoard = await response.json();
      
      set(state => ({
        currentBoard: state.currentBoard?.id === boardId ? updatedBoard : state.currentBoard,
        boards: state.boards.map(board => 
          board.id === boardId ? { ...board, ...updatedBoard } : board
        ),
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update board',
      });
      throw error;
    }
  },

  deleteBoard: async (boardId: string) => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_BASE_URL}/boards/${boardId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete board');
      }
      
      set(state => ({
        boards: state.boards.filter(board => board.id !== boardId),
        currentBoard: state.currentBoard?.id === boardId ? null : state.currentBoard,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete board',
      });
      throw error;
    }
  },

  joinBoard: async (boardId: string) => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        console.warn('No access token available for joining board');
        throw new Error('No access token available');
      }

      console.log('Joining board:', boardId);

      const response = await fetch(`${API_BASE_URL}/boards/${boardId}/join/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = 'Failed to join board';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.detail || errorMessage;
          console.error('Join board error:', response.status, errorData);
        } catch (e) {
          console.error('Join board error:', response.status, response.statusText);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Successfully joined board:', result);

      // Obtain a short-lived single-use ticket so the JWT never appears in the WS URL
      const ticketResponse = await fetch(`${API_BASE_URL}/boards/${boardId}/ws-ticket/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!ticketResponse.ok) {
        throw new Error('Failed to obtain WebSocket ticket');
      }

      const { ticket } = await ticketResponse.json();

      // Connect to WebSocket using the ticket (not the JWT)
      get().connectWebSocket(boardId, ticket);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to join board';
      console.error('Join board failed:', error);
      set({
        error: errorMessage,
      });
      // Don't throw error, just log it - board can still work without join
      console.warn('Continuing without board join...');
    }
  },

  leaveBoard: async (boardId: string) => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (token) {
        await fetch(`${API_BASE_URL}/boards/${boardId}/leave/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }

      // Disconnect WebSocket
      get().disconnectWebSocket();
    } catch (error) {
      console.error('Failed to leave board:', error);
    }
  },

  // WebSocket management
  connectWebSocket: (boardId: string, ticket: string) => {
    const { websocket } = get();

    // Detach handlers before closing to prevent stale onclose firing
    // Skip if the same socket is already connected/connecting for this board
    if (websocket) {
      if (
        websocket.url.includes(`/ws/board/${boardId}/`) &&
        (websocket.readyState === WebSocket.OPEN || websocket.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }
      websocket.onclose = null;
      websocket.onerror = null;
      websocket.close();
    }

    const ws = new WebSocket(`${WS_BASE_URL}/ws/board/${boardId}/?ticket=${ticket}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        handleWebSocketMessage(message, set, get);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      set({ websocket: null });
    };

    ws.onerror = () => {
      // Avoid logging the full URL (which contains the token)
      console.error('WebSocket error: connection failed');
      set({ error: 'WebSocket connection failed' });
    };

    set({ websocket: ws });
  },

  disconnectWebSocket: () => {
    const { websocket } = get();
    if (websocket) {
      websocket.onclose = null;
      websocket.onerror = null;
      websocket.close();
      set({ websocket: null, participants: new Map(), cursors: new Map() });
    }
  },

  sendWebSocketMessage: (message: any) => {
    const { websocket } = get();
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      websocket.send(JSON.stringify(message));
    }
  },

  // Card actions with API fallback
  createCard: async (columnId: string, content: string, position = { x: 0, y: 0 }) => {
    const { websocket } = get();
    
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      // Use WebSocket if available
      get().sendWebSocketMessage({
        action: 'create_card',
        column_id: columnId,
        content,
        position_x: position.x,
        position_y: position.y,
      });
    } else {
      // Fallback to REST API
      try {
        const token = useAuthStore.getState()?.tokens?.access;
        
        if (!token) {
          throw new Error('No access token available');
        }

        const response = await fetch(`${API_BASE_URL}/cards/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            column: columnId,
            content,
            position_x: position.x,
            position_y: position.y,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to create card');
        }

        // Refresh board to get updated data
        const { currentBoard } = get();
        if (currentBoard) {
          await get().fetchBoard(currentBoard.id);
        }
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Failed to create card',
        });
        throw error;
      }
    }
  },

  updateCard: async (cardId: string, data: Partial<Card>) => {
    const { websocket } = get();
    
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      // Use WebSocket if available
      get().sendWebSocketMessage({
        action: 'update_card',
        card_id: cardId,
        ...data,
      });
    } else {
      // Fallback to REST API
      try {
        const token = useAuthStore.getState()?.tokens?.access;
        
        if (!token) {
          throw new Error('No access token available');
        }

        const response = await fetch(`${API_BASE_URL}/cards/${cardId}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error('Failed to update card');
        }

        // Don't refresh board - local state is already updated
        // This prevents form reset while user is typing
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Failed to update card',
        });
        throw error;
      }
    }
  },

  deleteCard: async (cardId: string) => {
    const { websocket } = get();
    
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      // Use WebSocket if available
      get().sendWebSocketMessage({
        action: 'delete_card',
        card_id: cardId,
      });
    } else {
      // Fallback to REST API
      try {
        const token = useAuthStore.getState()?.tokens?.access;
        
        if (!token) {
          throw new Error('No access token available');
        }

        const response = await fetch(`${API_BASE_URL}/cards/${cardId}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete card');
        }

        // Refresh board to get updated data
        const { currentBoard } = get();
        if (currentBoard) {
          await get().fetchBoard(currentBoard.id);
        }
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Failed to delete card',
        });
        throw error;
      }
    }
  },

  moveCard: async (cardId: string, columnId: string, position: { x: number; y: number }, order: number) => {
    const { websocket } = get();
    
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      // Use WebSocket if available
      get().sendWebSocketMessage({
        action: 'move_card',
        card_id: cardId,
        column_id: columnId,
        position_x: position.x,
        position_y: position.y,
        order,
      });
    } else {
      // Fallback to REST API
      try {
        const token = useAuthStore.getState()?.tokens?.access;
        
        if (!token) {
          throw new Error('No access token available');
        }

        const response = await fetch(`${API_BASE_URL}/cards/${cardId}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            column: columnId,
            position_x: position.x,
            position_y: position.y,
            order,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to move card');
        }

        // Refresh board to get updated data
        const { currentBoard } = get();
        if (currentBoard) {
          await get().fetchBoard(currentBoard.id);
        }
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Failed to move card',
        });
        throw error;
      }
    }
  },

  voteCard: async (cardId: string, voteValue: number) => {
    const { websocket } = get();
    
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      // Use WebSocket if available
      get().sendWebSocketMessage({
        action: 'vote_card',
        card_id: cardId,
        vote_value: voteValue,
      });
    } else {
      // Fallback to REST API with optimistic update
      try {
        const token = useAuthStore.getState()?.tokens?.access;
        
        if (!token) {
          throw new Error('No access token available');
        }

        // Optimistic update - update UI immediately
        const { currentBoard } = get();
        if (currentBoard) {
          const updatedColumns = currentBoard.columns.map(column => ({
            ...column,
            cards: column.cards.map(card => {
              if (card.id === cardId) {
                return {
                  ...card,
                  vote_count: (card.vote_count || 0) + voteValue
                };
              }
              return card;
            })
          }));

          set({
            currentBoard: {
              ...currentBoard,
              columns: updatedColumns
            }
          });
        }

        const response = await fetch(`${API_BASE_URL}/cards/${cardId}/vote/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            vote_value: voteValue,
          }),
        });

        if (!response.ok) {
          // Rollback on error
          const { currentBoard } = get();
          if (currentBoard) {
            const revertedColumns = currentBoard.columns.map(column => ({
              ...column,
              cards: column.cards.map(card => {
                if (card.id === cardId) {
                  return {
                    ...card,
                    vote_count: (card.vote_count || 0) - voteValue
                  };
                }
                return card;
              })
            }));

            set({
              currentBoard: {
                ...currentBoard,
                columns: revertedColumns
              }
            });
          }
          throw new Error('Failed to vote on card');
        }

        // Get accurate data from response
        const result = await response.json();
        if (result.vote_count !== undefined) {
          const { currentBoard } = get();
          if (currentBoard) {
            const updatedColumns = currentBoard.columns.map(column => ({
              ...column,
              cards: column.cards.map(card => {
                if (card.id === cardId) {
                  return {
                    ...card,
                    vote_count: result.vote_count
                  };
                }
                return card;
              })
            }));

            set({
              currentBoard: {
                ...currentBoard,
                columns: updatedColumns
              }
            });
          }
        }
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Failed to vote on card',
        });
        throw error;
      }
    }
  },

  // Canvas card actions - Always use REST API for reliability
  createCanvasCard: async (columnId: string, cardData: {
    content: string;
    position_x: number;
    position_y: number;
    width?: number;
    height?: number;
    z_index?: number;
    color?: string;
  }): Promise<Card | null> => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      // Build request body, ensuring content is never null/undefined
      const requestBody: Record<string, unknown> = {
        column: columnId,
        content: cardData.content ?? '', // Ensure content is always a string
        position_x: cardData.position_x ?? 0,
        position_y: cardData.position_y ?? 0,
      };
      
      // Only add optional fields if they have values
      if (cardData.width !== undefined) requestBody.width = cardData.width;
      if (cardData.height !== undefined) requestBody.height = cardData.height;
      if (cardData.z_index !== undefined) requestBody.z_index = cardData.z_index;
      if (cardData.color !== undefined) requestBody.color = cardData.color;
      
      console.log('[createCanvasCard] Request body:', requestBody);

      const response = await fetch(`${API_BASE_URL}/cards/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[createCanvasCard] Full error response:', errorData);
        // Handle various error formats from DRF
        let errorMessage = 'Failed to create card';
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.column) {
          errorMessage = `Column error: ${Array.isArray(errorData.column) ? errorData.column.join(', ') : errorData.column}`;
        } else if (typeof errorData === 'object' && Object.keys(errorData).length > 0) {
          // Get first error from any field
          const firstKey = Object.keys(errorData)[0];
          const value = errorData[firstKey];
          errorMessage = `${firstKey}: ${Array.isArray(value) ? value.join(', ') : value}`;
        }
        console.error('[createCanvasCard] Error:', response.status, errorMessage);
        throw new Error(errorMessage);
      }

      const createdCard = await response.json();
      console.log('[createCanvasCard] Card created:', createdCard.id);

      // Transform to Card format and add to state
      const transformedCard: Card = {
        id: createdCard.id,
        content: createdCard.content,
        column: columnId,
        created_by: createdCard.created_by || {
          id: 'unknown',
          display_name: 'Unknown',
          initials: 'U',
          avatar_color: '#6B7280',
        },
        position_x: createdCard.position_x || 0,
        position_y: createdCard.position_y || 0,
        order: createdCard.order || 0,
        color: createdCard.color || '#FBBF24',
        width: createdCard.width,
        height: createdCard.height,
        z_index: createdCard.z_index,
        is_grouped: createdCard.is_grouped || false,
        group_id: createdCard.group_id,
        is_revealed: createdCard.is_revealed !== undefined ? createdCard.is_revealed : true,
        is_author_hidden: createdCard.is_author_hidden || false,
        votes: [],
        vote_count: createdCard.vote_count || 0,
        upvotes: createdCard.upvotes || 0,
        downvotes: createdCard.downvotes || 0,
        emoji_reactions: [],
        emoji_summary: [],
        created_at: createdCard.created_at || new Date().toISOString(),
        updated_at: createdCard.updated_at || new Date().toISOString(),
      };

      // Add card to state
      set((state: BoardState) => ({
        currentBoard: state.currentBoard ? {
          ...state.currentBoard,
          columns: state.currentBoard.columns.map(col =>
            col.id === columnId
              ? { ...col, cards: [...col.cards, transformedCard] }
              : col
          ),
        } : null,
      }));

      // Broadcast to other users via WebSocket
      const { websocket } = get();
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({
          action: 'broadcast_card_created',
          card: {
            id: createdCard.id,
            content: createdCard.content,
            column_id: columnId,
            created_by: typeof createdCard.created_by === 'object' ? createdCard.created_by?.id : createdCard.created_by,
            created_by_name: typeof createdCard.created_by === 'object' ? createdCard.created_by?.display_name : 'Unknown',
            position_x: createdCard.position_x,
            position_y: createdCard.position_y,
            width: createdCard.width,
            height: createdCard.height,
            z_index: createdCard.z_index,
            color: createdCard.color,
            is_grouped: createdCard.is_grouped,
            group_id: createdCard.group_id,
            vote_count: createdCard.vote_count,
            upvotes: createdCard.upvotes,
            downvotes: createdCard.downvotes,
            order: createdCard.order,
            is_author_hidden: createdCard.is_author_hidden || false,
            created_at: createdCard.created_at,
          },
        }));
      }

      return transformedCard;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create card';
      console.error('[createCanvasCard] Failed:', errorMessage);
      set({ error: errorMessage });
      return null;
    }
  },

  updateCanvasCard: async (cardId: string, updates: {
    content?: string;
    position_x?: number;
    position_y?: number;
    width?: number;
    height?: number;
    z_index?: number;
    color?: string;
  }): Promise<boolean> => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      // Filter out undefined values
      const filteredUpdates: Record<string, unknown> = {};
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined) {
          filteredUpdates[key] = value;
        }
      });

      if (Object.keys(filteredUpdates).length === 0) {
        return true; // Nothing to update
      }

      console.log('[updateCanvasCard] Updating card:', cardId, filteredUpdates);

      const response = await fetch(`${API_BASE_URL}/cards/${cardId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(filteredUpdates),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.error || 'Failed to update card';
        console.error('[updateCanvasCard] Error:', response.status, errorMessage);
        throw new Error(errorMessage);
      }

      const updatedCard = await response.json();
      console.log('[updateCanvasCard] Card updated:', updatedCard.id);

      // Update card in state
      set((state: BoardState) => ({
        currentBoard: state.currentBoard ? {
          ...state.currentBoard,
          columns: state.currentBoard.columns.map(col => ({
            ...col,
            cards: col.cards.map(card =>
              card.id === cardId
                ? {
                    ...card,
                    content: updatedCard.content !== undefined ? updatedCard.content : card.content,
                    position_x: updatedCard.position_x !== undefined ? updatedCard.position_x : card.position_x,
                    position_y: updatedCard.position_y !== undefined ? updatedCard.position_y : card.position_y,
                    width: updatedCard.width !== undefined ? updatedCard.width : card.width,
                    height: updatedCard.height !== undefined ? updatedCard.height : card.height,
                    z_index: updatedCard.z_index !== undefined ? updatedCard.z_index : card.z_index,
                    color: updatedCard.color !== undefined ? updatedCard.color : card.color,
                  }
                : card
            ),
          })),
        } : null,
      }));

      // Broadcast to other users via WebSocket
      const { websocket } = get();
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        const state = get();
        let existingCard: any = null;
        state.currentBoard?.columns.forEach(col => {
          col.cards.forEach(c => { if (c.id === cardId) existingCard = c; });
        });
        if (existingCard) {
          websocket.send(JSON.stringify({
            action: 'broadcast_card_updated',
            card: {
              id: cardId,
              content: updatedCard.content ?? existingCard.content,
              column_id: existingCard.column,
              created_by: typeof existingCard.created_by === 'object' ? existingCard.created_by?.id : existingCard.created_by,
              created_by_name: typeof existingCard.created_by === 'object' ? existingCard.created_by?.display_name : 'Unknown',
              position_x: updatedCard.position_x ?? existingCard.position_x,
              position_y: updatedCard.position_y ?? existingCard.position_y,
              width: updatedCard.width ?? existingCard.width,
              height: updatedCard.height ?? existingCard.height,
              z_index: updatedCard.z_index ?? existingCard.z_index,
              color: updatedCard.color ?? existingCard.color,
              is_grouped: existingCard.is_grouped,
              group_id: existingCard.group_id,
              vote_count: existingCard.vote_count,
              upvotes: existingCard.upvotes,
              downvotes: existingCard.downvotes,
              order: existingCard.order,
              is_author_hidden: existingCard.is_author_hidden,
              created_at: existingCard.created_at,
            },
          }));
        }
      }

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update card';
      console.error('[updateCanvasCard] Failed:', errorMessage);
      set({ error: errorMessage });
      return false;
    }
  },

  toggleCardVisibility: async (cardId: string): Promise<boolean> => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      if (!token) throw new Error('No access token available');

      const response = await fetch(`${API_BASE_URL}/cards/${cardId}/toggle_visibility/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) return false;

      const data = await response.json();
      set((state: BoardState) => {
        if (!state.currentBoard) return state;
        const updatedColumns = state.currentBoard.columns.map(col => ({
          ...col,
          cards: col.cards.map(card =>
            card.id === cardId ? { ...card, is_author_hidden: data.is_author_hidden } : card
          ),
        }));
        return { currentBoard: { ...state.currentBoard, columns: updatedColumns } };
      });
      return true;
    } catch {
      return false;
    }
  },

  // Column actions
  createColumn: async (boardId: string, data: Partial<Column>) => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_BASE_URL}/columns/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ ...data, board: boardId }),
      });

      if (!response.ok) {
        throw new Error('Failed to create column');
      }

      // Refresh board data
      await get().fetchBoard(boardId);
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create column',
      });
      throw error;
    }
  },

  updateColumn: async (columnId: string, data: Partial<Column>) => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_BASE_URL}/columns/${columnId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to update column');
      }

      const updatedColumn = await response.json();
      
      set(state => ({
        currentBoard: state.currentBoard ? {
          ...state.currentBoard,
          columns: state.currentBoard.columns.map(col => 
            col.id === columnId ? { ...col, ...updatedColumn } : col
          ),
        } : null,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update column',
      });
      throw error;
    }
  },

  deleteColumn: async (columnId: string) => {
    try {
      const token = useAuthStore.getState()?.tokens?.access;
      
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_BASE_URL}/columns/${columnId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete column');
      }
      
      set(state => ({
        currentBoard: state.currentBoard ? {
          ...state.currentBoard,
          columns: state.currentBoard.columns.filter(col => col.id !== columnId),
        } : null,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete column',
      });
      throw error;
    }
  },

  // Board controls
  toggleBoardLock: () => {
    const { currentBoard } = get();
    if (!currentBoard) return;

    const action = currentBoard.status === 'active' ? 'lock_board' : 'unlock_board';
    get().sendWebSocketMessage({ action });
  },

  updateCursorPosition: (x: number, y: number) => {
    get().sendWebSocketMessage({
      action: 'cursor_move',
      cursor_x: x,
      cursor_y: y,
    });
  },

  // Utility actions
  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    get().disconnectWebSocket();
    set({
      currentBoard: null,
      boards: [],
      loading: false,
      error: null,
      participants: new Map(),
      cursors: new Map(),
    });
  },
}));

const HIDDEN_CARD_CONTENT = '***';

function getCurrentUserId(): string | undefined {
  return useAuthStore.getState()?.user?.id;
}

function applyCardVisibilityMask(card: Card, currentUserId?: string): Card {
  const authorId = card.created_by?.id;
  if (card.is_author_hidden && currentUserId !== authorId) {
    return { ...card, content: HIDDEN_CARD_CONTENT };
  }
  return card;
}

function transformWsCard(card: WsCardPayload): Card {
  const columnId = card.column_id || card.column || '';
  const base: Card = {
    id: card.id,
    content: card.content,
    column: columnId,
    created_by: {
      id: card.created_by,
      display_name: card.created_by_name || 'Unknown',
      initials: (card.created_by_name || 'U').substring(0, 2).toUpperCase(),
      avatar_color: '#6B7280',
    } as User,
    position_x: card.position_x || 0,
    position_y: card.position_y || 0,
    order: card.order || 0,
    color: card.color || '#FBBF24',
    width: card.width,
    height: card.height,
    z_index: card.z_index,
    is_grouped: card.is_grouped || false,
    group_id: card.group_id ?? undefined,
    is_revealed: card.is_revealed !== undefined ? card.is_revealed : true,
    is_author_hidden: card.is_author_hidden || false,
    votes: [],
    vote_count: card.vote_count || 0,
    upvotes: card.upvotes || 0,
    downvotes: card.downvotes || 0,
    emoji_reactions: [],
    emoji_summary: [],
    created_at: card.created_at || new Date().toISOString(),
    updated_at: card.updated_at || new Date().toISOString(),
  };
  return applyCardVisibilityMask(base, getCurrentUserId());
}

function upsertCardInBoard(state: BoardState, transformedCard: Card): BoardState['currentBoard'] {
  if (!state.currentBoard) return null;
  const columnId = transformedCard.column;
  return {
    ...state.currentBoard,
    columns: state.currentBoard.columns.map(col => {
      if (col.id !== columnId) {
        return { ...col, cards: col.cards.filter(c => c.id !== transformedCard.id) };
      }
      const exists = col.cards.some(c => c.id === transformedCard.id);
      if (exists) {
        return {
          ...col,
          cards: col.cards.map(c => (c.id === transformedCard.id ? transformedCard : c)),
        };
      }
      return { ...col, cards: [...col.cards, transformedCard] };
    }),
  };
}

// WebSocket message handler
function handleWebSocketMessage(message: WebSocketMessage, set: any, get: any) {
  switch (message.type) {
    case 'card_created':
      handleCardCreated(message as CardCreatedMessage, set, get);
      break;
    case 'card_updated':
      handleCardUpdated(message as CardUpdatedMessage, set, get);
      break;
    case 'card_deleted':
      handleCardDeleted(message as CardDeletedMessage, set, get);
      break;
    case 'card_voted':
      handleCardVoted(message as CardVotedMessage, set, get);
      break;
    case 'card_moved':
      handleCardMoved(message as CardMovedMessage, set, get);
      break;
    case 'user_joined':
      handleUserJoined(message as UserJoinedMessage, set, get);
      break;
    case 'user_left':
      handleUserLeft(message as UserLeftMessage, set, get);
      break;
    case 'cursor_moved':
      handleCursorMoved(message as CursorMovedMessage, set, get);
      break;
    case 'board_locked':
      handleBoardLocked(message as BoardLockedMessage, set, get);
      break;
    case 'board_unlocked':
      handleBoardUnlocked(message as BoardUnlockedMessage, set, get);
      break;
    case 'celebration':
      set({
        celebrationEvent: {
          mode: (message as any).mode ?? 'confetti',
          triggeredBy: (message as any).triggered_by ?? 'Someone',
          id: Date.now(),
        },
      });
      break;
    case 'card_visibility_changed': {
      const { card_id, is_author_hidden, author_id, card: cardData } =
        message as CardVisibilityChangedMessage;
      set((state: BoardState) => {
        if (!state.currentBoard) return state;
        if (cardData) {
          const transformedCard = transformWsCard(cardData);
          return { currentBoard: upsertCardInBoard(state, transformedCard) };
        }
        const currentUserId = getCurrentUserId();
        const updatedColumns = state.currentBoard.columns.map(col => ({
          ...col,
          cards: col.cards.map(c => {
            if (c.id !== card_id) return c;
            return applyCardVisibilityMask(
              { ...c, is_author_hidden },
              currentUserId,
            );
          }),
        }));
        return { currentBoard: { ...state.currentBoard, columns: updatedColumns } };
      });
      break;
    }
    case 'canvas_elements_updated':
    case 'canvas_board_state':
      window.dispatchEvent(new CustomEvent('retrosive_canvas_ws', { detail: message }));
      break;
    default:
      console.log('Unknown message type:', message.type);
  }
}

function handleCardCreated(message: CardCreatedMessage, set: any, get: any) {
  const transformedCard = transformWsCard(message.card);
  set((state: BoardState) => ({
    currentBoard: upsertCardInBoard(state, transformedCard),
  }));
}

function handleCardUpdated(message: CardUpdatedMessage, set: any, get: any) {
  const transformedCard = transformWsCard(message.card);
  set((state: BoardState) => ({
    currentBoard: upsertCardInBoard(state, transformedCard),
  }));
}

function handleCardDeleted(message: CardDeletedMessage, set: any, get: any) {
  set((state: BoardState) => ({
    currentBoard: state.currentBoard ? {
      ...state.currentBoard,
      columns: state.currentBoard.columns.map(col => ({
        ...col,
        cards: col.cards.filter(card => card.id !== message.card_id),
      })),
    } : null,
  }));
}

function handleCardVoted(message: CardVotedMessage, set: any, get: any) {
  set((state: BoardState) => ({
    currentBoard: state.currentBoard ? {
      ...state.currentBoard,
      columns: state.currentBoard.columns.map(col => ({
        ...col,
        cards: col.cards.map(card => 
          card.id === message.card_id 
            ? { 
                ...card, 
                vote_count: message.vote_count,
                upvotes: message.upvotes,
                downvotes: message.downvotes,
              }
            : card
        ),
      })),
    } : null,
  }));
}

function handleCardMoved(message: CardMovedMessage, set: any, get: any) {
  const transformedCard = transformWsCard(message.card);

  set((state: BoardState) => {
    if (!state.currentBoard) return state;

    // Remove card from old column and add to new column
    const updatedColumns = state.currentBoard.columns.map(col => ({
      ...col,
      cards: col.cards.filter(c => c.id !== transformedCard.id),
    }));

    // Add card to new column
    const finalColumns = updatedColumns.map(col => 
      col.id === transformedCard.column
        ? { ...col, cards: [...col.cards, transformedCard] }
        : col
    );

    return {
      currentBoard: {
        ...state.currentBoard,
        columns: finalColumns,
      },
    };
  });
}

function handleUserJoined(message: UserJoinedMessage, set: any, get: any) {
  // Handle user join logic if needed
  console.log('User joined:', message.user_name);
}

function handleUserLeft(message: UserLeftMessage, set: any, get: any) {
  const { cursors } = get();
  cursors.delete(message.user_id);
  set({ cursors: new Map(cursors) });
}

function handleCursorMoved(message: CursorMovedMessage, set: any, get: any) {
  const { cursors } = get();
  cursors.set(message.user_id, {
    x: message.cursor_x,
    y: message.cursor_y,
    user: {
      id: message.user_id,
      display_name: message.user_name || 'Unknown',
      initials: (message.user_name || 'U').substring(0, 2).toUpperCase(),
      avatar_color: message.user_color || '#6B7280',
      avatar_image_url: message.user_avatar || null,
    } as User,
  });
  set({ cursors: new Map(cursors) });
}

function handleBoardLocked(message: BoardLockedMessage, set: any, get: any) {
  set((state: BoardState) => ({
    currentBoard: state.currentBoard ? {
      ...state.currentBoard,
      status: 'locked' as const,
    } : null,
  }));
}

function handleBoardUnlocked(message: BoardUnlockedMessage, set: any, get: any) {
  set((state: BoardState) => ({
    currentBoard: state.currentBoard ? {
      ...state.currentBoard,
      status: 'active' as const,
    } : null,
  }));
}

// Make auth store available globally for board store
if (typeof window !== 'undefined') {
  (window as any).__boardStore = useBoardStore;
} 